import {
  UserRoleId,
  ADMIN_ROLE_IDS,
  LHPH_ROLE_IDS,
  DEALER_ROLE_IDS,
} from '../enums/UserRoleId.enum';
import { AppUserRole } from '../types/AppUserRole.type';

/**
 * Who may open New Dealer Contact. A Dealer Admin may not: Kevin, 2026-10-22, contacts and
 * portal users are managed by "LHPH Staff", and the API refuses a dealer on every contact
 * route (lhphcapital-42bw). Until then a contact was the one thing a Dealer Admin still made.
 * @param currentUserRoleId - The role ID of the current user
 * @returns Array of role IDs the contact screen treats as creatable
 */
export const getContactCreatableRoles = (currentUserRoleId: number): number[] => {
  switch (currentUserRoleId) {
    case UserRoleId.SuperAdmin:
    case UserRoleId.Admin:
      return Array.from(ADMIN_ROLE_IDS).concat(
        Array.from(LHPH_ROLE_IDS),
        Array.from(DEALER_ROLE_IDS),
        [UserRoleId.Guest]
      );

    case UserRoleId.LHPHAdmin:
      return Array.from(LHPH_ROLE_IDS).concat(Array.from(DEALER_ROLE_IDS));

    case UserRoleId.DealerAdmin:
    case UserRoleId.LHPHUser:
    case UserRoleId.DealerUser:
    case UserRoleId.Guest:
    default:
      return [];
  }
};

/**
 * Determines which roles a user can create LOGINS for, based on their current role.
 *
 * A Dealer Admin creates none. Kevin, 2026-10-07 (lhphcapital-fdjb item 4): "LHPH logins
 * should be the only ones to create logins". The API refuses it too (authJwt.canCreateUser).
 * @param currentUserRoleId - The role ID of the current user
 * @returns Array of role IDs that the user can create
 */
export const getCreatableRoles = (currentUserRoleId: number): number[] =>
  getContactCreatableRoles(currentUserRoleId);

/** filterCreatableRoles for the New Dealer Contact screen. */
export const filterContactCreatableRoles = (
  allRoles: AppUserRole[],
  currentUserRoleId: number
): AppUserRole[] => {
  const ids = getContactCreatableRoles(currentUserRoleId);
  return allRoles.filter(role => ids.includes(role.id));
};

/**
 * Filters the available roles based on what the current user can create
 * @param allRoles - Array of all available roles
 * @param currentUserRoleId - The role ID of the current user
 * @returns Filtered array of roles that the user can create
 */
export const filterCreatableRoles = (
  allRoles: AppUserRole[],
  currentUserRoleId: number
): AppUserRole[] => {
  const creatableRoleIds = getCreatableRoles(currentUserRoleId);
  return allRoles.filter(role => creatableRoleIds.includes(role.id));
};

/**
 * Checks if a user can create users with a specific role
 * @param currentUserRoleId - The role ID of the current user
 * @param targetRoleId - The role ID of the user to be created
 * @returns boolean indicating if the user can create the target role
 */
export const canCreateRole = (currentUserRoleId: number, targetRoleId: number): boolean => {
  const creatableRoleIds = getCreatableRoles(currentUserRoleId);
  return creatableRoleIds.includes(targetRoleId);
};

/**
 * Checks if a user has admin-like permissions
 * @param currentUserRoleId - The role ID of the current user
 * @returns boolean indicating if the user has admin access
 */
export const hasAdminAccess = (currentUserRoleId: number): boolean => {
  return ADMIN_ROLE_IDS.has(currentUserRoleId);
};

/**
 * Checks if a user can manage a specific user based on their role
 * @param currentUserRoleId - The role ID of the current user
 * @param targetUserRoleId - The role ID of the user to be managed
 * @returns boolean indicating if the user can manage the target user
 */
export const canManageUser = (currentUserRoleId: number, targetUserRoleId: number): boolean => {
  // SuperAdmin and Admin can manage all users
  if (ADMIN_ROLE_IDS.has(currentUserRoleId)) {
    return true;
  }

  // LHPH Admin can manage LHPH users and dealer users
  if (currentUserRoleId === UserRoleId.LHPHAdmin) {
    return LHPH_ROLE_IDS.has(targetUserRoleId) || DEALER_ROLE_IDS.has(targetUserRoleId);
  }

  // Every other role, Dealer Admin included, manages no logins. The API has refused a
  // Dealer Admin's user edits and deletes since #527 (lhphcapital-fdjb); a branch here
  // would only advertise a dead action on some future screen.
  return false;
};
